import { html, PropertyValues, render, RootPart, TemplateResult } from 'lit';
import { Constructor } from 'lit-element';
import { property, query, queryAll, state } from 'lit/decorators.js';
import { GridColumnElement } from '@vaadin/vaadin-grid/vaadin-grid-column';
import { GridItemModel } from '@vaadin/vaadin-grid';
import { GzDataTableWebComponent } from '../component/GzDataTableWebComponent';
import {
  DataTableDisplayType,
  DataTableEvents,
  DefaultColumnKeys,
  IColumnOption,
  IColumnType,
  IDataTableColumn,
} from '../types';
import { ButtonColor, ButtonType, FontSize } from '../../gzIconButton/types';
import { InputWidthValues } from '../../gzInput';
import { InputTypes } from '../../gzInput/types';
import { GzFormFieldWebComponent } from '../../gzForm/formField';
import { RequiredValidator } from '../../gzForm/validators/required.validator';
import { FormValidator } from '../../gzForm/formField/types/form-validator';
import { FontSizes, LabelWidthValues } from '../../../types/common/types';
import { debounce } from 'lodash';
import { GzToast, ToastSeverity } from '../../gzToastNotification/type';
import { GzToastNotificationWebComponent } from '../../gzToastNotification';
import { elementClassListAdd, elementClassListRemove } from '../../../utils/htmlElement.utils';
import { ROW_EDIT_MODE } from '../types/constants';

// all public attributes and methods from the decorator class should be exposed in the interface
export interface RowEditingInterface {
  allowRowEditing: boolean;
}

// all protected attributes and methods from the decorator class, used by other decorators, should be exposed here
export interface RowEditingProtectedInterface {
  rowEditingDisabled: boolean;
  editingRowIndex: number | undefined;
}

export const rowEditing = <T extends Constructor<GzDataTableWebComponent<TDataTableItem>>, TDataTableItem = unknown>(
  dataTableClass: T
) => {
  class RowEditingDecorator extends dataTableClass {
    /**
     * If set, it adds on each row an edit button on the <code>DefaultColumnKeys.ACTIONS</code> column, which allows
     * editing the row
     */
    @property({ type: Boolean, attribute: true, reflect: true })
    allowRowEditing = false;

    /**
     * If set, the extra validation function is called when the user tries to save the edited changes, which compares the
     * edited row with all the other rows in the table and performs a custom validation
     */
    @property({ type: Function, attribute: true, reflect: true })
    extraValidator:
      | ((
          newItem: TDataTableItem,
          oldItem: TDataTableItem,
          rowIndex: number,
          currentItems: TDataTableItem[]
        ) => boolean)
      | undefined;

    private validateChangesButtonDisabled = false;

    @state()
    protected editingRowIndex: number | undefined;

    @state()
    protected rowEditingDisabled = false;

    @queryAll('.edit-form-field')
    private feedBackFormFieldList: GzFormFieldWebComponent[];

    @queryAll('.cell-label--header')
    private headerColumnsElementList: HTMLElement[];

    @query('#validateEditButton')
    private validateEditBtnElement: HTMLElement;

    @query('#validateEditTooltip')
    private validateEditTooltipElement: HTMLElement;

    private columnWidths: { width: number; id: string }[] = [];

    private onInputChangeHandler = debounce(() => this.onInputChange(), 300);

    updated(changedProperties: PropertyValues): void {
      super.updated(changedProperties);

      if (changedProperties.has('displayType') && this.displayType !== DataTableDisplayType.AUTO_WIDTH) {
        this.saveColumnWidths();
      }
      if (changedProperties.has('currentItems') && this.editingRowIndex !== undefined) {
        this.handleCancelEditing(this.editingRowIndex);
      }

      this.updateComplete.then(() => {
        this.setValidators();
      });
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
      super.firstUpdated(_changedProperties);
      if (this.displayType === DataTableDisplayType.AUTO_WIDTH) {
        this.updateComplete.then(() => {
          setTimeout(() => {
            this.saveColumnWidths();
          }, 0);
        });
      }
    }

    private saveColumnWidths(): void {
      this.headerColumnsElementList.forEach(header => {
        this.columnWidths.push({ width: header.clientWidth, id: this.getKeyFromId(header.id) });
      });
    }

    // Helper function to extract key from id
    private getKeyFromId(id: string): string {
      return id.split('-')[0];
    }

    private getRowElementByIndex(index: number): HTMLElement | undefined | null {
      return this.grid.shadowRoot?.querySelector(`tr[aria-rowindex="${index + 2}"]`);
    }

    private getTableElement(): HTMLElement | undefined | null {
      return this.grid.shadowRoot?.querySelector('table');
    }

    private setEditingStyles(isEditing: boolean, rowIndex: number): void {
      const rowElement = this.getRowElementByIndex(rowIndex);
      const table = this.getTableElement();
      if (!rowElement || !table) {
        return;
      }
      if (isEditing) {
        elementClassListAdd(rowElement, ROW_EDIT_MODE);
      } else {
        elementClassListRemove(rowElement, ROW_EDIT_MODE);
      }
      if (this.displayType !== DataTableDisplayType.AUTO_WIDTH) {
        table.style.overflow = isEditing ? 'visible' : 'auto';
      }
    }

    private setValidators(): void {
      this.feedBackFormFieldList.forEach(feedBackField => {
        feedBackField.Validators = this.getValidators(feedBackField);
      });
    }

    private getValidators(feedBackField: GzFormFieldWebComponent): FormValidator[] {
      const fieldId = this.getKeyFromId(feedBackField.id);
      const columnValidators = this.columns.find(column => column.key === fieldId)?.validators;
      const defaultValidator = RequiredValidator(this.strings.requiredFieldMessage);
      return columnValidators ?? [defaultValidator];
    }

    private handleEnterEditing(rowIndex: number): void {
      this.setEditingState(true, rowIndex);
      this.dispatchEvent(
        new CustomEvent(DataTableEvents.ROW_EDITING_STARTED, {
          detail: { index: rowIndex, item: this.currentItems[rowIndex] },
          bubbles: true,
          composed: true,
        })
      );
    }

    private handleCancelEditing(rowIndex: number): void {
      this.setEditingState(false, rowIndex);
      this.dispatchEvent(
        new CustomEvent(DataTableEvents.ROW_EDITING_CANCELLED, {
          detail: { index: rowIndex, item: this.currentItems[rowIndex] },
          bubbles: true,
          composed: true,
        })
      );
    }

    private handleValidateEditing(rowIndex: number): void {
      const item = this.currentItems[rowIndex];
      const newItems = this.currentItems;

      if (!this.checkIfAllFieldsAreValid()) {
        return;
      }
      if (!this.performExtraValidation(item, rowIndex)) {
        this.showToastNotification();
        return;
      }

      this.editItem(item);

      newItems[rowIndex] = item;
      this.currentItems = newItems;
      this.setEditingState(false, rowIndex);

      this.dispatchEvent(
        new CustomEvent(DataTableEvents.ROW_EDITED, {
          detail: { index: rowIndex, item },
          bubbles: true,
          composed: true,
        })
      );
    }

    private editItem(item: TDataTableItem): void {
      this.feedBackFormFieldList.forEach(feedBackField => {
        const key = this.getKeyFromId(feedBackField.id);
        item[key] = this.sanitizeValue(feedBackField.value, key);
      });
    }

    private performExtraValidation(item: TDataTableItem, rowIndex: number): boolean {
      if (!this.extraValidator) {
        return true;
      }
      const oldItem = { ...item };
      const editedItem = { ...item };
      this.feedBackFormFieldList.forEach(feedBackField => {
        const key = this.getKeyFromId(feedBackField.id);
        editedItem[key] = this.sanitizeValue(feedBackField.value, key);
      });
      return this.extraValidator(editedItem, oldItem, rowIndex, this.currentItems);
    }

    private sanitizeValue(value: unknown, columnKey: string): unknown {
      const column = this.columns.find(col => col.key === columnKey);
      return column?.sanitizer ? column.sanitizer(value) : value;
    }

    private showToastNotification(): void {
      const toastNotificationConfigs: GzToast = {
        message: this.strings.extraValidationToastMessage,
        hasAction: false,
        severity: ToastSeverity.ERROR,
      };
      GzToastNotificationWebComponent.buildToastWrapperElement(toastNotificationConfigs);
    }

    protected renderColumn(column: IDataTableColumn, index: number): GridColumnElement {
      // overridden function
      const columnElement = super.renderColumn(column, index);
      if (this.allowRowEditing && column.key === DefaultColumnKeys.ACTIONS) {
        Object.assign(columnElement, {
          resizable: false,
          flexGrow: 0,
        });
      }
      return columnElement;
    }

    protected renderColumnCell(
      root: HTMLElement,
      rowData: GridItemModel | undefined,
      column: IDataTableColumn
    ): RootPart | undefined {
      // overridden function
      if (!rowData) {
        return;
      }
      if (this.allowRowEditing && column.renderer) {
        const content = (rowData.item as GridItemModel)[column.key];
        const editMode = this.editingRowIndex === rowData.index;
        return render(column.renderer?.(content, column, rowData.index, editMode, rowData.item), root);
      }
      if (this.allowRowEditing && column.key === DefaultColumnKeys.ACTIONS) {
        return render(
          html`
            <div class="actions-cell">${this.renderColumnCellElements('', column, rowData.index).map(e => e)}</div>
          `,
          root
        );
      }
      return super.renderColumnCell(root, rowData, column);
    }

    protected renderColumnCellElements(content: unknown, column: IDataTableColumn, rowIndex: number): TemplateResult[] {
      // overridden function
      if (this.allowRowEditing && column.key === DefaultColumnKeys.ACTIONS) {
        return this.editingRowIndex === rowIndex
          ? this.renderEditingModeButtons(rowIndex)
          : [...super.renderColumnCellElements(content, column), this.renderEditButton(rowIndex)];
      }

      if (this.allowRowEditing && this.editingRowIndex === rowIndex && column.columnType !== IColumnType.DISPLAY) {
        return [this.renderFormField(content, column, rowIndex)];
      }
      return super.renderColumnCellElements(content, column, rowIndex);
    }

    private renderFormField(content: unknown, column: IDataTableColumn, rowIndex: number): TemplateResult {
      let input: TemplateResult | null = null;
      switch (column.columnType) {
        case IColumnType.TEXT:
          input = this.renderTextInput(content as string, column);
          break;
        case IColumnType.SINGLE_SELECT:
          input = this.renderSingleSelectInput(content as number | string, column);
          break;
        case IColumnType.MULTI_SELECT:
          input = this.renderMultiSelectInput(content as (number | string)[], column);
          break;
        case IColumnType.NUMBER:
          input = this.renderNumberInput(content as number, column);
          break;
      }
      return html`
        <gz-form-field label="" id="${column.key}-row${rowIndex}-form-field" class="edit-form-field" required>
          ${input}
        </gz-form-field>
      `;
    }

    private renderTextInput(content: string, column: IDataTableColumn): TemplateResult {
      return html`
        <gz-input
          gz-form-input
          type="${InputTypes.TEXT}"
          inputWidth="${this.getInputWidth(column)}"
          placeholder="${this.strings.textInputPlaceholder}"
          .value="${content}"
          @change="${this.onInputChangeHandler.bind(this)}"
        ></gz-input>
      `;
    }

    private renderSingleSelectInput(content: number | string, column: IDataTableColumn): TemplateResult {
      return html`
        <gz-single-select
          gz-form-input
          class="dropdown"
          inputWidth="${InputWidthValues.FULL}"
          labelWidth="${LabelWidthValues.FULL}"
          .filterOptions="${column.options}"
          .value="${content}"
          ?cannotDelete="${true}"
          @change="${this.onInputChangeHandler.bind(this)}"
        ></gz-single-select>
      `;
    }

    private renderMultiSelectInput(content: (number | string)[], column: IDataTableColumn): TemplateResult {
      const options = column.options?.map(
        option => ({ ...option, selected: content.includes(option.value) } as IColumnOption)
      );
      return html`
        <gz-enum-filter
          gz-form-input
          class="dropdown"
          inputWidth="${InputWidthValues.FULL}"
          labelWidth="${LabelWidthValues.FULL}"
          allItemsLabel="${this.strings.dropdownAllItemsLabel}"
          confirmActionButtonString="${this.strings.dropdownApplyButtonString}"
          .filterOptions="${options}"
          @change="${this.onInputChangeHandler.bind(this)}"
        ></gz-enum-filter>
      `;
    }

    private renderNumberInput(content: number, column: IDataTableColumn): TemplateResult {
      return html`
        <gz-input-number
          gz-form-input
          inputWidth="${this.getInputWidth(column)}"
          placeholder="${this.strings.textInputPlaceholder}"
          .min="${column.minValue}"
          .max="${column.maxValue}"
          .unitSuffix="${column.numberSuffix}"
          .value="${content}"
          @change="${this.onInputChangeHandler.bind(this)}"
        ></gz-input-number>
      `;
    }

    private getInputWidth(column: IDataTableColumn) {
      if (this.displayType !== DataTableDisplayType.AUTO_WIDTH) {
        return InputWidthValues.FULL;
      }

      const ranges = [
        { limit: parseInt(InputWidthValues.MEDIUM, 10), value: InputWidthValues.SMALL },
        { limit: parseInt(InputWidthValues.LARGE, 10), value: InputWidthValues.MEDIUM },
        { limit: parseInt(InputWidthValues.XLARGE, 10), value: InputWidthValues.LARGE },
      ];

      const columnWidth = this.columnWidths.find(width => width.id === column.key)?.width;

      if (columnWidth) {
        for (const element of ranges) {
          if (columnWidth < element.limit) {
            return element.value;
          }
        }
      }

      return InputWidthValues.FULL;
    }

    private onInputChange(): void {
      if (!this.checkIfAllFieldsAreValid()) {
        this.validateEditBtnElement.setAttribute('disabled', 'true');
        this.validateEditTooltipElement.setAttribute('tooltipText', this.strings.disabledEditTooltip);
      } else {
        this.validateEditBtnElement.removeAttribute('disabled');
        this.validateEditTooltipElement.setAttribute('tooltipText', this.strings.validateEditTooltip);
      }
    }

    private checkIfAllFieldsAreValid(): boolean {
      let hasErrors = false;
      this.feedBackFormFieldList.forEach(feedBackField => {
        if (!feedBackField.valid) {
          hasErrors = true;
        }
      });

      return !hasErrors;
    }

    private renderEditButton(rowIndex: number): TemplateResult {
      return html` <gz-tooltip
        tooltipText="${this.editingRowIndex === undefined ? this.strings.editRowTooltip : ''}"
        class="action-btn-tooltip"
      >
        <gz-icon-button
          icon="ds-icon-edit_pen"
          fontSize="21"
          ?disabled="${this.rowEditingDisabled || this.editingRowIndex !== undefined}"
          class="action-icon edit-icon"
          buttonType="${ButtonType.SQUARE}"
          buttonColor="${ButtonColor.SECONDARY}"
          @click="${() => this.handleEnterEditing(rowIndex)}"
        ></gz-icon-button>
      </gz-tooltip>`;
    }

    private renderEditingModeButtons(rowIndex: number): TemplateResult[] {
      return [
        html`
          <gz-tooltip
            id="validateEditTooltip"
            tooltipText="${this.strings.validateEditTooltip}"
            class="action-btn-tooltip"
          >
            <gz-icon-button
              icon="ds-icon-notification_success"
              id="validateEditButton"
              fontSize="${FontSizes.BODY}"
              buttonfill="white"
              ?disabled="${this.validateChangesButtonDisabled}"
              class="action-icon edit-icon"
              buttonColor="${ButtonColor.SUCCESS}"
              @click="${() => this.handleValidateEditing(rowIndex)}"
            ></gz-icon-button>
          </gz-tooltip>
        `,
        html`
          <gz-tooltip tooltipText="${this.strings.cancelEditTooltip}" class="action-btn-tooltip">
            <gz-icon-button
              icon="ds-icon-delete_large"
              fontSize="${FontSize.ICON_SIZE_20}"
              class="action-icon edit-icon"
              buttonColor="${ButtonColor.SECONDARY}"
              @click="${() => this.handleCancelEditing(rowIndex)}"
            ></gz-icon-button>
          </gz-tooltip>
        `,
      ];
    }

    private setEditingState(isEditing: boolean, rowIndex: number): void {
      this.setEditingStyles(isEditing, rowIndex);
      this.editingRowIndex = isEditing ? rowIndex : undefined;
      this.instance.rowDeletionDisabled = isEditing;
      this.instance.rowReorderingDisabled = isEditing;
      this.instance.rowSelectionDisabled = isEditing;
      this.grid.notifyResize();
    }
  }

  return RowEditingDecorator;
};
